// @flow

import React, { Component, Fragment } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { change, reduxForm } from 'redux-form';
import { type DispatchType } from 'types/redux';
// https://github.com/erikras/redux-form/issues/3630#issue-276018629
import type { FormProps } from 'redux-form/lib/types.js.flow';
import { type StateType as ReducerStateType } from 'reducers';
import get from 'lodash/get';
import moment from 'moment';
import Fieldset from '@clark-inc/clark-forms';
import { Row, Col, Button, BreadcrumbsHeader } from 'clark-styles';

import { deleteSession } from 'actions/sessions';

import DeleteSessionModal from 'containers/sessions/delete-modal';

import { isSessionReportRatingEnabled } from 'utils/helpers/sessions/business-settings';

import type { EngagementsType } from 'types/engagements';
import type { SessionType, SessionsType } from 'types/sessions';
import { type ClientsType } from 'types/clients';
import { type SubjectsType } from 'types/subjects';

import { getEngagementClientDetails, getEngagementSubjectDetails } from 'utils/helpers/engagements';
import {
  formatSeriesItem,
  getRemainingRecurringSessions,
  getSessionDuration,
  editRecurringEnabled,
} from 'utils/helpers/sessions';
import combineDateAndTimeObject from 'utils/helpers/combine-date-time';

import {
  Header,
  GridWrapper,
  InfoItem,
  InfoCopy,
  Item,
  RecurringSessionsContainer,
  RecurringSessionsHeader,
  SubjectIcon,
} from './styles';
import formatDateFields from './date-fields/fieldset';
import formatReportFields from './report-fields/fieldset';

const SESSION_EDIT_FORM_ID = 'sessionEdit';

const RecurringSessionsList = ({ remainingRecurringSessions }) => (
  <RecurringSessionsContainer>
    <RecurringSessionsHeader>
      {`You are editing ${remainingRecurringSessions.length} sessions in this series`}
    </RecurringSessionsHeader>
    {remainingRecurringSessions.map(s => (
      <Item key={s.id}>{formatSeriesItem(s.attributes)}</Item>
    ))}
  </RecurringSessionsContainer>
);

type OwnPropsType = {
  clients: ClientsType,
  engagements: EngagementsType,
  sessions: SessionsType,
  sessionId: ?string,
  subjects: SubjectsType,
};

const isActionable = (billCreatedAt: ?string, sessionCreatedAt: string) =>
  !billCreatedAt || billCreatedAt < sessionCreatedAt;

const mapStateToProps = (
  { form: { sessionEdit }, router: { location } }: ReducerStateType,
  ownProps: OwnPropsType,
) => {
  const { clients, engagements, sessions, sessionId, subjects } = ownProps;
  const session = sessions[sessionId || ''];
  const {
    attributes: { tutoringEngagementId, createdAt: sessionCreatedAt, billCreatedAt },
  } = session;

  const engagement = engagements[tutoringEngagementId];

  const isUneditable = !!billCreatedAt;
  const isSchedulable = isActionable(billCreatedAt, sessionCreatedAt);

  const defaultSessionDuration = isUneditable
    ? getSessionDuration(session)
    : engagement.attributes.sessionLengthInMinutes;
  const { clientName } = getEngagementClientDetails(engagement, clients);
  const { subjectName } = getEngagementSubjectDetails(engagement, subjects);

  const shouldUpdateFutureSessions = !!get(location, 'state.shouldUpdateFutureSessions');
  const recurringSessions = get(location, 'state.sessions');

  return {
    sessionCreatedAt,
    billCreatedAt,
    clientName,
    defaultSessionDuration,
    engagement,
    formValues: sessionEdit && sessionEdit.values,
    isSchedulable,
    isUneditable,
    recurringSessions,
    session,
    shouldUpdateFutureSessions,
    subjectName,
  };
};

type DispatchPropsType = {
  dispatchDeleteSession: typeof deleteSession,
};

const mapDispatchToProps = { dispatchDeleteSession: deleteSession };

type FormValuesType = {
  endTime: { time: string, period: string },
  internalNotes: ?string,
  sessionReportBody: ?string,
  sessionReportRating: ?number,
  startDate: { date: string },
  startTime: { time: string, period: string },
  locationRaw: ?string,
};

type PropsType = {
  billCreatedAt: string,
  sessionCreatedAt: string,
  clientName: string,
  defaultSessionDuration: moment$MomentDuration,
  dispatch: DispatchType,
  formValues: FormValuesType,
  handleClick: any,
  handleSubmit: any,
  isUneditable: boolean,
  isSchedulable: boolean,
  pristine: boolean,
  recurringSessions: SessionsType,
  session: SessionType,
  sessionId: string,
  shouldUpdateFutureSessions: boolean,
  subjectName: string,
  business: {
    id: string,
  },
} & FormProps &
  DispatchPropsType;

type StateType = {
  startTime: string,
  endTime: string,
};

class SessionEditForm extends Component<PropsType, StateType> {
  componentDidUpdate(prevProps) {
    const { formValues } = this.props;

    if (formValues !== prevProps.formValues) {
      const { dispatch, isUneditable } = this.props;
      const getDate = get(formValues, 'startDate.date');
      const getStart = get(formValues, 'startTime');
      const getStartTime = get(formValues, 'startTime.time');
      const getEndTime = get(formValues, 'endTime.time');
      const getStartTimePeriod = get(formValues, 'startTime.period');
      const getEndTimePeriod = get(formValues, 'endTime.period');

      const hasSessionDurationInputValues =
        getDate && getStartTime && getEndTime && getStartTimePeriod && getEndTimePeriod;

      if (hasSessionDurationInputValues) {
        const startDateTime = combineDateAndTimeObject(getDate, getStart);
        if (isUneditable) {
          const { defaultSessionDuration } = this.props;
          const next = moment(startDateTime)
            .add(defaultSessionDuration, 'minutes')
            .format('h:mm a')
            .split(' ');

          dispatch(change(SESSION_EDIT_FORM_ID, 'endTime[time]', next[0], true, true));
          dispatch(change(SESSION_EDIT_FORM_ID, 'endTime[period]', next[1], true, true));
        }
      }
    }
  }

  deleteSession = (shouldUpdateFutureSessions: boolean) => {
    const {
      session: { id: resourceId },
      dispatchDeleteSession,
    } = this.props;
    dispatchDeleteSession(resourceId, shouldUpdateFutureSessions);
  };

  render() {
    const {
      sessionCreatedAt,
      billCreatedAt,
      clientName,
      subjectName,
      handleClick,
      handleSubmit,
      isSchedulable,
      isUneditable,
      recurringSessions,
      session,
      sessionId,
      shouldUpdateFutureSessions,
      pristine,
      valid,
      business: { id: businessId },
    } = this.props;

    const {
      attributes: { startTime, status, serviceSessionRecurrenceGroupingId },
    } = session;
    const isEditRecurringEnabled =
      editRecurringEnabled(
        session,
        recurringSessions,
        serviceSessionRecurrenceGroupingId,
        startTime,
      ) && shouldUpdateFutureSessions;

    const FIELDSET = formatDateFields(isUneditable, isSchedulable, isEditRecurringEnabled);
    const breadcrumbs = [{ path: `/sessions/${sessionId}`, displayName: 'Back' }];
    const title = isEditRecurringEnabled ? 'Edit Series' : 'Edit Session';
    const remainingRecurringSessions = getRemainingRecurringSessions(session, recurringSessions);
    const formattedStartDate = moment(startTime).format('MMM Do');
    const isDeletable = isActionable(billCreatedAt, sessionCreatedAt);
    const isRateable = isSessionReportRatingEnabled(businessId);

    return (
      <Fragment>
        <BreadcrumbsHeader breadcrumbs={breadcrumbs} title={title} />
        <form>
          <GridWrapper>
            <Row>
              <Col sm={12} mdOffset={3} md={6} lgOffset={3} lg={6}>
                <Header>{`${clientName}'s Session on ${formattedStartDate}`}</Header>
                {subjectName && (
                  <InfoItem>
                    <SubjectIcon />
                    <InfoCopy>{subjectName}</InfoCopy>
                  </InfoItem>
                )}
                {isEditRecurringEnabled && (
                  <RecurringSessionsList
                    session={session}
                    remainingRecurringSessions={remainingRecurringSessions}
                  />
                )}
                <Fieldset data={FIELDSET} />
                {!isEditRecurringEnabled && (
                  <Fieldset
                    data={formatReportFields(status === 'canceled', clientName, isRateable)}
                  />
                )}
                <Button
                  layout="fullWidth"
                  disabled={pristine || !valid}
                  type="submit"
                  onClick={handleSubmit(handleClick(status, isEditRecurringEnabled))}
                >
                  Save
                </Button>
                {isDeletable && (
                  <DeleteSessionModal
                    session={session}
                    disabled={false}
                    isButton={false}
                    deleteRecurringEnabled={isEditRecurringEnabled}
                    remainingRecurringSessions={remainingRecurringSessions}
                  />
                )}
              </Col>
            </Row>
          </GridWrapper>
        </form>
      </Fragment>
    );
  }
}

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  reduxForm({
    form: SESSION_EDIT_FORM_ID,
  }),
)(SessionEditForm);



// WEBPACK FOOTER //
// ./src/containers/sessions/edit-form/index.js